/* eslint-disable react/no-unstable-nested-components */
import type { FC, ReactElement } from "react"
import { Fragment } from "react"

import isUndefined from "lodash-es/isUndefined"
import { NavLink } from "react-router-dom"

import {
    Divider,
    EDividerColor,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"

import classes from "./coach.navigation-stepper.styles"
import {
    ECoachNavigationStepperBulletCompletion as ECompletion,
    ECoachNavigationStepperBulletOrder as EOrder,
    ECoachNavigationStepperMode as EStepperMode,
    type TCoachNavigationStepperBullet as TBullet
} from "./coach.navigation-stepper.types"

type TProps = TBullet & {
    stepperMode: EStepperMode
    isLast: boolean
    isNextCompleted: boolean
}

const CoachNavigationStepperItem: FC<TProps> = ({
    stepperMode,
    isLast = false,
    isNextCompleted,
    pageId,
    onHover = undefined,
    href,
    title,
    subTitle,
    order,
    completion
}: TProps): ReactElement => {
    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isMobile: boolean = breakpoints.xs || breakpoints.sm
    const isTablet: boolean = breakpoints.md || breakpoints.lg
    const isDesktop: boolean = breakpoints.xl || breakpoints.xxl

    function BulletContainer({ children }: { children: ReactElement }): ReactElement {
        if (!isUndefined(href))
            return (
                <NavLink
                    data-index={pageId}
                    to={href}
                    onFocus={onHover}
                    onMouseOver={onHover}
                    className={classes.bulletContainer({ order, stepperMode })}
                >
                    {children}
                </NavLink>
            )

        return <div className={classes.bulletContainer({ order, stepperMode })}>{children}</div>
    }

    return (
        <Fragment>
            <div className={classes.bulletHolder()}>
                <BulletContainer>
                    <div className={classes.bullet.default({ order, completion, stepperMode })}>
                        <div className={classes.bulletMark({ order, completion })} />
                    </div>
                </BulletContainer>

                {title || subTitle ? (
                    <div className={classes.bulletTitleContainer()}>
                        {title && (
                            <Typography
                                className={classes.bulletText()}
                                text={title}
                                size={isMobile ? ETypographySize.Tiny : ETypographySize.Medium}
                                family={isMobile ? ETypographyFamily.Nunito : ETypographyFamily.DmSerif}
                                weight={isMobile ? ETypographyWeight.Default : ETypographyWeight.Medium}
                                alignment={ETypographyAlignment.Center}
                                color={((): ETypographyColor => {
                                    if (isMobile) return ETypographyColor.Black

                                    if (order === EOrder.Current) return ETypographyColor.Dark

                                    return ETypographyColor.Gray23
                                })()}
                            />
                        )}

                        {subTitle && !isMobile && (
                            <Typography
                                className={classes.bulletText()}
                                text={subTitle}
                                size={ETypographySize.Tiny}
                                color={ETypographyColor.Gray23}
                                alignment={ETypographyAlignment.Center}
                            />
                        )}
                    </div>
                ) : null}
            </div>

            {!isLast && (
                <Fragment>
                    <Divider
                        className={classes.stepper.dividerSmall({
                            isDesktop,
                            isTablet,
                            isModulesCut: stepperMode === EStepperMode.Modules
                        })}
                        variant={
                            completion === ECompletion.Completed || isNextCompleted
                                ? EDividerColor.SemiDarkBlue
                                : EDividerColor.MediumBlue
                        }
                        margins={{ top: "15px", bottom: "auto" }}
                    />
                    <Divider
                        className={classes.stepper.divider({ isSessionsCut: false, isModulesCut: false })}
                        variant={
                            completion === ECompletion.Completed ? EDividerColor.SemiDarkBlue : EDividerColor.MediumBlue
                        }
                        margins={{ top: "15px", bottom: "auto" }}
                    />
                </Fragment>
            )}
        </Fragment>
    )
}

CoachNavigationStepperItem.displayName = "CoachNavigationStepperItem"

export default CoachNavigationStepperItem
